import _ from 'underscore';
import {
  AlertCenter,
  type IAlert,
  type IAlertLevel,
} from '@bingads-webui/alert-center';
import type {
  AlertCollectionInput,
  AlertId,
  AlertMessage,
  AlertObjectInput,
  CcuiAlert,
  CcuiAlertCenterOptions,
} from './types';

const alertLevel: Record<'error' | 'warning' | 'info' | 'success', IAlertLevel> = {
  error: 'Error',
  warning: 'Warning',
  info: 'Info',
  success: 'Confirmation',
};

export class CcuiAlertCenter extends AlertCenter {
  /**
   * Note: The new alert banner will override the one with same id,
   * so when you want to use functions below,
   * please make sure you only need one banner for one call or show the first one is as expected.
   * Otherwise please customize in your feature
   * @param {Object} i18n - The i18n object that contains legacy keys Alert_CarouselPagination and Button_Got_it.
   */
  constructor({
    i18n,
  }: CcuiAlertCenterOptions = {}) {
    super({ useCarouselForErrors: true, i18n });
  }

  show(alert: CcuiAlert, id?: AlertId): string {
    return super.show({
      hasIcon: true,
      ...alert,
    } as IAlert, id as string | undefined);
  }

  appendError(errors?: AlertCollectionInput, id?: AlertId): void {
    if (_.isArray(errors) && !_.isEmpty(errors) && (errors[0] as AlertMessage).Message) {
      this.appendErrorMessage((errors[0] as AlertMessage).Message, id);
    }
  }

  appendWarning(warnings?: AlertCollectionInput, id?: AlertId): void {
    if (_.isArray(warnings) && !_.isEmpty(warnings) && (warnings[0] as AlertMessage).Message) {
      this.appendWarningMessage((warnings[0] as AlertMessage).Message, id);
    }
  }

  appendInfo(info?: AlertObjectInput, id?: AlertId): void {
    if (_.isObject(info) && (info as AlertMessage).Message) {
      this.appendInfoMessage((info as AlertMessage).Message, id);
    }
  }

  appendSuccess(confirmation?: AlertObjectInput, id?: AlertId): void {
    if (_.isObject(confirmation) && (confirmation as AlertMessage).Message) {
      this.appendSuccessMessage((confirmation as AlertMessage).Message, id);
    }
  }

  appendInfoMessage(infoMessage: string, id?: AlertId): void {
    this.show({
      level: alertLevel.info,
      dismissible: false,
      id,
      message: infoMessage,
    }, id);
  }

  appendWarningMessage(warningMessage: string, id?: AlertId): void {
    this.show({
      level: alertLevel.warning,
      dismissible: true,
      id,
      message: warningMessage,
    }, id);
  }

  appendErrorMessage(errorMessage: string, id?: AlertId): void {
    this.show({
      level: alertLevel.error,
      dismissible: false,
      id,
      message: errorMessage,
    }, id);
  }

  appendSuccessMessage(confirmationMessage: string, id?: AlertId): void {
    this.show({
      level: alertLevel.success,
      dismissible: true,
      id,
      message: confirmationMessage,
    }, id);
  }
}
